<template>
  <Drawer
    v-model:visible="isVisible"
    position="right"
    :header="editMode ? 'Edit Benefit' : 'Add Benefit'"
    :style="{ width: '500px' }"
  >
    <div class="space-y-6">
      <div class="space-y-2">
        <div>
          <label
            for="benefit-service"
            class="text-sm font-medium text-gray-900"
          >
            Services <span class="text-red-500">*</span>
          </label>
          <small class="block text-gray-600"
            >Which services do you want to discount?</small
          >
        </div>
        <MultiSelect
          ref="selectRef"
          v-model="formData.services"
          input-id="benefit-service"
          :options="serviceOptions"
          option-label="label"
          option-value="value"
          placeholder="Select"
          display="chip"
          class="w-full"
          :loading="isLoadingServices"
          :show-toggle-all="false"
          :disabled="editMode"
          :pt="{
            overlay: {
              style: 'max-width: min(460px, calc(100vw - 2rem))',
            },
          }"
          @show="shouldAutoOpen = false"
          @click="handleSelectClick"
        >
          <template #header>
            <div class="flex items-center gap-2 px-3 py-2" @click.stop>
              <Checkbox
                input-id="profile-benefit-services-select-all"
                :model-value="areAllServicesSelected"
                binary
                :disabled="editMode || serviceOptions.length === 0"
                @update:model-value="toggleAllServices"
              />
              <label
                for="profile-benefit-services-select-all"
                class="text-sm font-medium text-gray-700"
              >
                Select All
              </label>
            </div>
          </template>
        </MultiSelect>
      </div>

      <div class="space-y-2">
        <label for="benefit-discount" class="text-sm font-medium text-gray-900">
          Discount <span class="text-red-500">*</span>
        </label>
        <Select
          v-model="formData.discount_percentage"
          input-id="benefit-discount"
          :options="discountOptions"
          option-label="label"
          option-value="value"
          placeholder="Select"
          class="w-full"
        />
      </div>

      <div class="space-y-2">
        <div>
          <label
            for="benefit-limit-type"
            class="text-sm font-medium text-gray-900"
          >
            Limit Type <span class="text-red-500">*</span>
          </label>
          <small class="block text-gray-600"
            >How frequently can they use this benefit?</small
          >
        </div>
        <Select
          v-model="formData.limit_type"
          input-id="benefit-limit-type"
          :options="limitTypeOptions"
          option-label="label"
          option-value="value"
          placeholder="Select"
          class="w-full"
        />
      </div>

      <div v-if="formData.limit_type === 'recurring'" class="space-y-2">
        <label
          for="benefit-renewal-cadence"
          class="text-sm font-medium text-gray-900"
        >
          Renewal Cadence <span class="text-red-500">*</span>
        </label>
        <Select
          v-model="formData.renewal_cadence"
          input-id="benefit-renewal-cadence"
          :options="renewalCadenceOptions"
          option-label="label"
          option-value="value"
          placeholder="Select"
          class="w-full"
        />
      </div>

      <div class="space-y-2">
        <div>
          <label
            for="benefit-quantity"
            class="text-sm font-medium text-gray-900"
          >
            Quantity <span v-if="!isUnlimited" class="text-red-500">*</span>
          </label>
          <small
            v-if="formData.limit_type === 'recurring'"
            class="block text-gray-600"
            >Unused benefits do not carry over.</small
          >
        </div>
        <div v-if="!isUnlimited" class="flex">
          <InputNumber
            v-model="formData.quantity"
            input-id="benefit-quantity"
            input-class="rounded-r-none w-full"
            class="flex-grow"
            :min="1"
            :max="1000"
            @input="handleQuantityInput"
          />
          <InputGroupAddon
            class="border border-l-0 rounded-r px-3 flex items-center whitespace-nowrap"
            style="background-color: #e5e7eb"
          >
            {{ quantityPlaceholder }}
          </InputGroupAddon>
        </div>
        <InputText v-else value="N/A" disabled class="w-full" />
      </div>

      <div class="flex justify-end">
        <Button
          label="Done"
          :disabled="!isFormValid"
          :loading="isSaving"
          @click="handleSave"
        />
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from "vue";
import Drawer from "primevue/drawer";
import Button from "primevue/button";
import Select from "primevue/select";
import MultiSelect from "primevue/multiselect";
import InputNumber from "primevue/inputnumber";
import InputText from "primevue/inputtext";
import InputGroupAddon from "primevue/inputgroupaddon";
import Checkbox from "primevue/checkbox";
import type { AdminSubscriberBenefit, LimitTypeEnum } from "@/api/model";
import {
  getDiscountPercentageOptions,
  getRenewalCadenceOptions,
  formatRenewalCadence,
} from "../helpers";
import { useServices } from "@/modules/bookings/composables/useServices";
import { useManualBenefit } from "../composables/useManualBenefit";

const props = defineProps<{
  visible: boolean;
  editMode?: boolean;
  initialData?: AdminSubscriberBenefit;
  profileId: number;
}>();

const emit = defineEmits<{
  "update:visible": [value: boolean];
  saved: [];
}>();

const { services, fetchServices, isLoadingServices } = useServices();
const { create, update, isLoading: isSaving } = useManualBenefit();
const selectRef = ref();
const shouldAutoOpen = ref(false);

const formData = ref<{
  services: string[];
  discount_percentage: number | null;
  limit_type: LimitTypeEnum | null;
  quantity: number | null;
  renewal_cadence: string | null;
}>({
  services: [],
  discount_percentage: null,
  limit_type: null,
  quantity: null,
  renewal_cadence: null,
});

const isVisible = computed({
  get: () => props.visible,
  set: (value) => emit("update:visible", value),
});

const serviceOptions = computed(() => {
  const options = services.value
    .filter((s) => s.title && s.title.trim().length > 0)
    .map((service) => ({
      label: service.title,
      value: service.id,
    }));

  if (
    props.editMode &&
    props.initialData?.service_summaries &&
    props.initialData.service_summaries.length > 0
  ) {
    props.initialData.service_summaries.forEach((serviceSummary) => {
      if (!options.some((option) => option.value === serviceSummary.id)) {
        options.unshift({
          label: serviceSummary.title,
          value: serviceSummary.id,
        });
      }
    });
  }

  return options;
});

const areAllServicesSelected = computed(() => {
  const optionValues = serviceOptions.value.map((option) => option.value);
  return (
    optionValues.length > 0 &&
    optionValues.every((value) => formData.value.services.includes(value))
  );
});

const discountOptions = computed(() => getDiscountPercentageOptions());
const renewalCadenceOptions = computed(() => getRenewalCadenceOptions());

const limitTypeOptions = [
  { label: "Unlimited", value: "unlimited" },
  { label: "Recurring", value: "recurring" },
  { label: "Fixed Limit", value: "fixed" },
];

const isUnlimited = computed(() => formData.value.limit_type === "unlimited");

const quantityPlaceholder = computed(() => {
  if (
    formData.value.limit_type === "recurring" &&
    formData.value.renewal_cadence
  ) {
    const label = formatRenewalCadence(formData.value.renewal_cadence);
    return label ? `Per ${label}` : "Per period";
  }
  return "Total";
});

const isFormValid = computed(() => {
  if (formData.value.services.length === 0) return false;
  if (formData.value.discount_percentage === null) return false;
  if (!formData.value.limit_type) return false;
  if (
    !isUnlimited.value &&
    (formData.value.quantity === null || formData.value.quantity <= 0)
  )
    return false;
  if (
    formData.value.limit_type === "recurring" &&
    !formData.value.renewal_cadence
  )
    return false;
  return true;
});

watch(
  () => formData.value.limit_type,
  (newLimitType) => {
    if (newLimitType === "unlimited") {
      formData.value.quantity = null;
    }
    if (newLimitType !== "recurring") {
      formData.value.renewal_cadence = null;
    }
  }
);

watch(
  () => props.visible,
  (visible) => {
    if (visible && props.editMode && props.initialData) {
      formData.value = {
        services: [...props.initialData.services],
        discount_percentage: props.initialData.discount_percentage,
        limit_type: props.initialData.limit_type,
        quantity: props.initialData.quantity_total ?? null,
        renewal_cadence: props.initialData.renewal_cadence ?? null,
      };
    } else if (visible && !props.editMode) {
      formData.value = {
        services: [],
        discount_percentage: null,
        limit_type: null,
        quantity: null,
        renewal_cadence: null,
      };
    }
  }
);

async function handleSave() {
  if (!isFormValid.value) return;

  let success: boolean;

  if (props.editMode && props.initialData) {
    success = await update(props.profileId, props.initialData.id, {
      discount_percentage: formData.value.discount_percentage!,
      limit_type: formData.value.limit_type!,
      quantity_total: isUnlimited.value ? null : formData.value.quantity,
      renewal_cadence:
        formData.value.limit_type === "recurring"
          ? formData.value.renewal_cadence
          : null,
    });
  } else {
    success = await create(props.profileId, {
      services: formData.value.services,
      discount_percentage: formData.value.discount_percentage!,
      limit_type: formData.value.limit_type!,
      quantity: isUnlimited.value ? null : formData.value.quantity,
      renewal_cadence:
        formData.value.limit_type === "recurring"
          ? formData.value.renewal_cadence
          : null,
    });
  }

  if (success) {
    emit("saved");
    isVisible.value = false;
  }
}

function toggleAllServices(checked: boolean) {
  if (props.editMode) return;
  formData.value.services = checked
    ? serviceOptions.value.map((option) => option.value)
    : [];
}

function handleSelectClick() {
  if (isLoadingServices.value) {
    shouldAutoOpen.value = true;
  }
}

function handleQuantityInput(event: any) {
  formData.value.quantity = event.value;
}

watch(isLoadingServices, async (loading, wasLoading) => {
  if (wasLoading && !loading && shouldAutoOpen.value && selectRef.value) {
    shouldAutoOpen.value = false;
    await nextTick();
    selectRef.value.show?.();
  }
});

watch(
  () => props.visible,
  (visible) => {
    if (visible) {
      fetchServices({ limit: 1000, offset: 0 });
    }
  }
);

onMounted(() => {
  if (services.value.length === 0) {
    fetchServices({ limit: 1000, offset: 0 });
  }
});
</script>
